import * as _ from "lodash";
import * as React from "react";
import * as S from "@styled";

import Select, { MultiAsyncSelect } from "../../../shared/components/select/Select";

import { ChangeEvent } from "../../../shared/model/Event";
import { Option } from "../../../shared/model/Option";
import { Utilities } from "../../../shared/utilities";
import { AppContext } from "../../App";
import { AgentGroupWithProcesses, IAppReducerState, IProcessTriggerValueOption, IRuleData } from "../../shared/model";
import { aggregates, expressionOperators, operators, timePeriods, timePeriodTypes } from "../../shared/ruleEditorMappings";
import { INewRuleFormData } from "../RuleEditor";
import { useParams } from "react-router-dom";
import { CustomBooleanSwitch } from "shared/components/CustomSwitch";
import LabelWithTooltip from "shared/components/LabelWithTooltip";
import { selectHeight } from "shared/components/select/model";

const GeneralSettings: React.FC<IRuleEditorGeneralSettingsProps> = (props) => {

    const { state, dispatch } = React.useContext<IAppReducerState>(AppContext);
    const { environmentName } = useParams<{ environmentName: string }>();
    const {
        formData,
        formDataErrors,
        recipients,
        defineFilterExpression,
        handlers
    } = props;

    const processOptions = async (searchString: string) => {
        try {
            const doesSearchStringHaveWhitespaceOrEmpty = searchString === "" || searchString.indexOf(' ') >= 0;
            const agentGroupsWithProcesses = await Utilities.getFetch<AgentGroupWithProcesses[]>(`api/processList/getMonitoringRuleProcessesByName?environmentName=${environmentName}${!doesSearchStringHaveWhitespaceOrEmpty ? `&searchString=${searchString}` : ""}`);
            return agentGroupsWithProcesses
                .map((agr) => ({
                    label: `Agent group - ${agr.agentGroupDisplayName}`,
                    options: agr.processes.map((process) => ({
                        label: `${process.name} - ${agr.agentGroupDisplayName}`,
                        // As we can have multiple Processes with the same name in different Agent groups we need to "uniqueify" them in the value
                        // so react-select will show them if the user wants to trigger multiple Processes with the same name.
                        // The value is cleaned up in the backend
                        value: `${process.uniqueIdentifier}_${agr.agentGroupName}`,
                        agentGroupName: agr.agentGroupName
                    }))
                }));
        } catch (e) {
            console.error(e);
            Utilities.toastError(e);
            return [];
        }
    };

    const getMappedProcessTriggerValues = () => {
        return formData?.processTriggerValues?.map((triggerValue) => ({
            label: `${triggerValue.processName} - ${triggerValue.agentGroupDisplayName}`,
            value: `${triggerValue.processGuid}_${triggerValue.agentGroupName}`, // The value has to be unique, react-select removes duplicates
            agentGroupName: triggerValue.agentGroupName
        }));
    };

    return (
        <>
            {props.asNewRule && (
                <S.FormGroup variant={formDataErrors.includes("name") ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                    <S.Label>
                        {FRENDS.Localizations.Name}
                    </S.Label>
                    <S.Input
                        id="name-input"
                        type="text"
                        name="ruleName"
                        onChange={handlers.handleFormDataName}
                        isFullWidth={true}
                        required={true}
                        defaultValue={formData.name as string}
                    />
                    {formDataErrors.includes("name") ? <span className="error">Name is required</span> : null}
                </S.FormGroup>
            )}

            <S.FormGroup>
                <S.Label>
                    {FRENDS.Localizations.Recipients} (comma separated values)
                </S.Label>
                <div className="rule-form-field">
                    <S.Input
                        type="text"
                        name="recipients"
                        isFullWidth={true}
                        onChange={handlers.handleRecipients}
                        defaultValue={recipients}
                    />
                </div>
            </S.FormGroup>

            <S.FormGroup>
                <LabelWithTooltip
                    label="Triggered Processes"
                    description={`Processes to trigger when this rule is tripped.\n
                    You can enable Processes to be usable for Monitoring rules in the Process Editor by enabling it in the Manual Trigger.`}
                />
                <MultiAsyncSelect
                    isMulti={true}
                    defaultValue={getMappedProcessTriggerValues()}
                    placeholder="Start typing a Process name"
                    cacheOptions={true}
                    defaultOptions={true}
                    noOptionsMessage={() => "No Processes found configured for triggering - enable Process triggering from the Process' Manual Trigger switch"}
                    loadOptions={processOptions}
                    onChange={handlers.handleProcessTriggerValue}
                />

            </S.FormGroup>
            <S.FormGroup>
                <S.Label>{FRENDS.Localizations.Rule_FilterPromotedVariableValues}</S.Label>
                <CustomBooleanSwitch
                    toggled={defineFilterExpression}
                    onChange={handlers.handleDefineFilterExpression}
                />
            </S.FormGroup>
            <S.FormGroup variant={formDataErrors.includes("expressionExpectedValue") || formDataErrors.includes("variableName") ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                <LabelWithTooltip
                    label={FRENDS.Localizations.Rule_VariableToMonitor}
                    description={defineFilterExpression ?
                        "Promoted variable values that match the filter are the only ones taken into account when calculating alert rules."
                        : undefined}
                />
                <S.InputGroup style={{ alignItems: "flex-start", justifyContent: "flex-start" }}>
                    <S.FlexItem style={{ flexBasis: "33.33%", flexGrow: 0 }}>
                        <Select
                            id={`select-promoted-variable-${props?.rule?.id ?? "newrule"}`}
                            classNamePrefix="react-select"
                            options={state.promotedVariables}
                            className={formDataErrors.includes("variableName") ? "select-error" : ""}
                            menuPlacement={props.asNewRule ? "bottom" : "top"}
                            value={Utilities.valueFromId(state.promotedVariables, formData.variableName)}
                            hasError={formDataErrors.includes("variableName")}
                            borderRadius={defineFilterExpression ? "25px 0px 0px 25px" : "25px"}
                            onChange={handlers.handleVariableToMonitor}
                            placeholder={FRENDS.Localizations.Select + " " + FRENDS.Localizations.Rule_Variable.toLowerCase()}
                        />
                        {formDataErrors.includes("variableName") && <span className="error">The variable to monitor is required.</span>}
                    </S.FlexItem>
                    {defineFilterExpression &&
                        <>
                            <S.FlexItem style={{ flexBasis: "33.33%", flexGrow: 0 }}>
                                <Select
                                    id={`select-expression-operator-${props?.rule?.id ?? "newrule"}`}
                                    classNamePrefix="react-select"
                                    options={expressionOperators}
                                    value={Utilities.valueFromId(expressionOperators, formData.expressionOperator)}
                                    borderRadius={formData.expressionOperator !== "Exists" ? "0" : "0px 25px 25px 0px"}
                                    borderWidthLeft="0px !important"
                                    borderWidthRight={formData.expressionOperator !== "Exists" ? "0" : "2px"}
                                    onChange={handlers.handleExpressionOperator}
                                />
                            </S.FlexItem>
                            {formData.expressionOperator !== "Exists" &&
                                <S.FlexItem style={{ flexBasis: "33.33%", flexGrow: 0 }}>
                                    <S.Input
                                        type="text"
                                        isFullWidth={true}
                                        style={{ borderRadius: "0 25px 25px 0", height: selectHeight, minHeight: selectHeight }}
                                        name="ruleExpressionExpectedValue"
                                        required={formData.expressionOperator !== "Exists"}
                                        onChange={handlers.handleExpressionExpectedValue}
                                        defaultValue={formData.expressionExpectedValue as string}
                                        placeholder={FRENDS.Localizations.ExpectedValue}
                                    />
                                    {formDataErrors.includes("expressionExpectedValue") && <span className="error">The expressions expected value is required.</span>}
                                </S.FlexItem>
                            }
                        </>
                    }
                </S.InputGroup>
            </S.FormGroup>
            <S.FormGroup variant={formDataErrors.includes("expressionExpectedValue") || formDataErrors.includes("expectedValue") ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                <S.Label>{FRENDS.Localizations.Rule_SendAlertIf}</S.Label>
                <S.InputGroup style={{ alignItems: "flex-start" }}>
                    <S.FlexItem>
                        <Select
                            id={`select-aggregate-${props?.rule?.id ?? "newrule"}`}
                            classNamePrefix="react-select"
                            options={aggregates}
                            menuPlacement={props.asNewRule ? "bottom" : "top"}
                            value={Utilities.valueFromId(aggregates, formData.aggregate)}
                            borderRadius="25px 0px 0px 25px"
                            onChange={handlers.handleAggregate}
                        />
                    </S.FlexItem>
                    <S.FlexItem>
                        <Select
                            id={`select-operator-${props?.rule?.id ?? "newrule"}`}
                            classNamePrefix="react-select"
                            key="operators"
                            options={operators}
                            menuPlacement={props.asNewRule ? "bottom" : "top"}
                            value={Utilities.valueFromId(operators, formData.operator)}
                            borderRadius={"0"}
                            borderWidthLeft={"0px !important"}
                            borderWidthRight={"0px !important"}
                            onChange={handlers.handleOperator}
                        />
                    </S.FlexItem>
                    <S.FlexItem>
                        <S.Input
                            type="number"
                            step="0.1"
                            name="ruleExpectedValue"
                            isFullWidth={true}
                            required={true}
                            style={{ borderRadius: "0 25px 25px 0", height: selectHeight, minHeight: selectHeight }}
                            onChange={handlers.handleExpectedValue}
                            placeholder={FRENDS.Localizations.ExpectedValue}
                            defaultValue={formData.expectedValue as number}
                        />
                        {formDataErrors.includes("expectedValue") && <span className="error">The expected value is required.</span>}
                    </S.FlexItem>
                </S.InputGroup>
            </S.FormGroup>

            <S.FormGroup variant={formDataErrors.includes("timePeriod") ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                <S.Label>{FRENDS.Localizations.Rule_DuringTimeInterval}</S.Label>
                <S.InputGroup style={{ alignItems: "flex-start", justifyContent: "flex-start" }}>
                    <S.FlexItem style={{ flexBasis: "33.33%", flexGrow: 0 }}>
                        {formData.timePeriodType === "Minutes" && (
                            <>
                                <Select
                                    id={`select-time-period-${props?.rule?.id ?? "newrule"}`}
                                    classNamePrefix="react-select"
                                    options={timePeriods}
                                    menuPlacement={props.asNewRule ? "bottom" : "top"}
                                    value={Utilities.valueFromId(timePeriods, formData.timePeriod)}
                                    borderRadius="25px 0px 0px 25px"
                                    onChange={handlers.handleTimePeriod as any}
                                />
                                {formDataErrors.includes("timePeriod") && <span className="error">The time period is required.</span>}
                            </>
                        )}
                        {formData.timePeriodType !== "Minutes" && (
                            <>
                                <S.Input
                                    type="number"
                                    min={1}
                                    isFullWidth={true}
                                    max={24}
                                    style={{ borderRadius: "25px 0px 0px 25px", height: selectHeight, minHeight: selectHeight }}
                                    className="form-control"
                                    name="ruleTimePeriod"
                                    onChange={handlers.handleTimePeriod}
                                    defaultValue={formData.timePeriod as number}
                                    required={true}
                                />
                                {formDataErrors.includes("timePeriod") && <span className="error">The time period is required and cannot exceed 24 hours.</span>}
                            </>
                        )}
                    </S.FlexItem>
                    <S.FlexItem style={{ flexBasis: "33.33%", flexGrow: 0 }}>
                        <Select
                            id={`select-time-period-type-${props?.rule?.id ?? "newrule"}`}
                            classNamePrefix="react-select"
                            options={timePeriodTypes}
                            menuPlacement={props.asNewRule ? "bottom" : "top"}
                            value={Utilities.valueFromId(timePeriodTypes, formData.timePeriodType)}
                            borderRadius="0px 25px 25px 0px"
                            borderWidthLeft="0px !important"
                            onChange={handlers.handleTimePeriodType}
                        />
                    </S.FlexItem>
                </S.InputGroup>
            </S.FormGroup>
        </>
    );
};

interface IRuleEditorGeneralSettingsProps {
    rule?: IRuleData;
    asEditRule?: boolean;
    asNewRule?: boolean;
    formData: INewRuleFormData;
    formDataErrors: string[];
    recipients: string | undefined;
    defineFilterExpression: boolean;
    handlers: {
        handleFormDataName: (e: ChangeEvent<HTMLInputElement>) => void;
        handleEnvironmentChange: (e: Option) => void;
        handleRecipients: (e: ChangeEvent<HTMLInputElement>) => void;
        handleProcessTriggerValue: (triggerValues: IProcessTriggerValueOption[]) => void;
        handleVariableToMonitor: (e: Option) => void;
        handleDefineFilterExpression: () => void;
        handleExpressionOperator: (e: Option) => void;
        handleExpressionExpectedValue: (e: ChangeEvent<HTMLInputElement>) => void;
        handleAggregate: (e: Option) => void;
        handleOperator: (e: Option) => void;
        handleExpectedValue: (e: ChangeEvent<HTMLInputElement>) => void;
        handleTimePeriod: (e: ChangeEvent<HTMLInputElement>) => void;
        handleTimePeriodType: (e: Option) => void;
    };
}

export default GeneralSettings;
